import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminMarketingClient, publicMarketingClient } from '@/api/grpc/services';
import { getErrorMessage } from '@/utils/errorMessages';

const VISITOR_HASH_STORAGE_KEY = 'guardian_visitor_hash_v1';
const BLOG_VISIT_TRACK_CACHE_KEY = 'guardian_blog_last_visit_by_article_v1';
const BLOG_VISIT_DEDUP_WINDOW_MS = 30 * 60 * 1000;

const getOrCreateVisitorHash = (): string => {
  try {
    const existing = localStorage.getItem(VISITOR_HASH_STORAGE_KEY);
    if (existing) return existing;

    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    localStorage.setItem(VISITOR_HASH_STORAGE_KEY, generated);
    return generated;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

const shouldTrackVisitForBlog = (blogId: string): boolean => {
  if (!blogId) return false;

  try {
    const now = Date.now();
    const rawCache = localStorage.getItem(BLOG_VISIT_TRACK_CACHE_KEY);
    const cache: Record<string, number> = rawCache ? JSON.parse(rawCache) : {};
    const lastTrackedAt = cache[blogId];

    if (typeof lastTrackedAt === 'number' && now - lastTrackedAt < BLOG_VISIT_DEDUP_WINDOW_MS) {
      return false;
    }

    cache[blogId] = now;
    localStorage.setItem(BLOG_VISIT_TRACK_CACHE_KEY, JSON.stringify(cache));
    return true;
  } catch {
    // Fall back to backend dedupe when localStorage is unavailable.
    return true;
  }
};

// Admin Hooks
export function useAdminBlogs(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['admin-blogs', page, limit],
    queryFn: async () => {
      try {
        const response = await adminMarketingClient.listBlogs({ page, limit });
        return response;
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function useCreateBlog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      try {
        return await adminMarketingClient.createBlog(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['public-blogs'] });
    },
  });
}

export function useUpdateBlog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      try {
        return await adminMarketingClient.updateBlog(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['public-blogs'] });
    },
  });
}

export function useDeleteBlog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        return await adminMarketingClient.deleteBlog({ id });
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['public-blogs'] });
    },
  });
}

export function useAdminLeads(page = 1, limit = 50) {
  return useQuery({
    queryKey: ['admin-leads', page, limit],
    queryFn: async () => {
      try {
        const response = await adminMarketingClient.listLeads({ page, limit });
        return response;
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function useAdminCompanyAbout() {
  return useQuery({
    queryKey: ['admin-company-about'],
    queryFn: async () => {
      try {
        return await adminMarketingClient.getCompanyAbout({});
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function useUpsertCompanyAbout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      title: string;
      subtitle?: string;
      body: string;
      heroImageUrl?: string;
      websiteUrl?: string;
      linkedinUrl?: string;
      xUrl?: string;
      githubUrl?: string;
      googleBusinessUrl?: string;
      googleMapsUrl?: string;
      yelpUrl?: string;
    }) => {
      try {
        return await adminMarketingClient.upsertCompanyAbout(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-company-about'] });
      queryClient.invalidateQueries({ queryKey: ['public-team-page'] });
    },
  });
}

export function useAdminTeamMembers(page = 1, limit = 100, includeInactive = true) {
  return useQuery({
    queryKey: ['admin-team-members', page, limit, includeInactive],
    queryFn: async () => {
      try {
        return await adminMarketingClient.listTeamMembers({ page, limit, includeInactive });
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function useCreateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      fullName: string;
      roleTitle: string;
      bio: string;
      photoUrl?: string;
      websiteUrl?: string;
      linkedinUrl?: string;
      xUrl?: string;
      githubUrl?: string;
      displayOrder: number;
      isActive: boolean;
    }) => {
      try {
        return await adminMarketingClient.createTeamMember(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-team-members'] });
      queryClient.invalidateQueries({ queryKey: ['public-team-page'] });
    },
  });
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id: string;
      fullName?: string;
      roleTitle?: string;
      bio?: string;
      photoUrl?: string;
      websiteUrl?: string;
      linkedinUrl?: string;
      xUrl?: string;
      githubUrl?: string;
      displayOrder?: number;
      isActive?: boolean;
    }) => {
      try {
        return await adminMarketingClient.updateTeamMember(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-team-members'] });
      queryClient.invalidateQueries({ queryKey: ['public-team-page'] });
    },
  });
}

export function useDeleteTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        return await adminMarketingClient.deleteTeamMember({ id });
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-team-members'] });
      queryClient.invalidateQueries({ queryKey: ['public-team-page'] });
    },
  });
}

export function useBlogAnalytics(blogId: string) {
  return useQuery({
    queryKey: ['blog-analytics', blogId],
    queryFn: async () => {
      try {
        return await adminMarketingClient.getBlogAnalytics({ blogId });
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    enabled: !!blogId,
  });
}

// Public Hooks
export function usePublicBlogs(page = 1, limit = 12) {
  return useQuery({
    queryKey: ['public-blogs', page, limit],
    queryFn: async () => {
      try {
        const response = await publicMarketingClient.listBlogs({ page, limit });
        return response;
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function usePublicBlogBySlug(slug: string) {
  return useQuery({
    queryKey: ['public-blog', slug],
    queryFn: async () => {
      try {
        return await publicMarketingClient.getBlogBySlug({ slug });
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
    enabled: !!slug,
  });
}

export function useSubmitLead() {
  return useMutation({
    retry: 0,
    mutationFn: async (data: { email: string; name?: string; source: string }) => {
      try {
        return await publicMarketingClient.submitLead(data);
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function useTrackVisit() {
  return useMutation({
    retry: 0,
    mutationFn: async (blogId: string) => {
      if (!shouldTrackVisitForBlog(blogId)) {
        return;
      }

      const visitorHash = getOrCreateVisitorHash();
      try {
        return await publicMarketingClient.trackVisit(
          { blogId },
          { headers: { 'x-visitor-hash': visitorHash } }
        );
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Failed to track visit:', err);
        }
      }
    },
  });
}

export function usePublicTeamPage() {
  return useQuery({
    queryKey: ['public-team-page'],
    queryFn: async () => {
      try {
        return await publicMarketingClient.getTeamPage({});
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function usePublicWorkInPublicConfig() {
  return useQuery({
    queryKey: ['public-work-in-public-config'],
    queryFn: async () => {
      try {
        return await publicMarketingClient.getWorkInPublicConfig({});
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}

export function usePublicEngineConsoleGuideConfig() {
  return useQuery({
    queryKey: ['public-engine-console-guide-config'],
    queryFn: async () => {
      try {
        return await publicMarketingClient.getEngineConsoleGuideConfig({});
      } catch (err) {
        throw new Error(getErrorMessage(err, 'marketing'));
      }
    },
  });
}
